import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { getStories, deleteStory, getNovels } from '../../services/api';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, LogOut, FileText, BookOpen, User, Mail, Book } from 'lucide-react';

const AdminDashboard = () => {
  const [stories, setStories] = useState([]);
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { logout } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchData = async () => {
    try {
      const category = filter === 'all' ? null : filter;
      const [storiesData, novelsData] = await Promise.all([
        getStories(category),
        getNovels()
      ]);
      setStories(storiesData);
      setNovels(novelsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (storyId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await deleteStory(storyId);
      toast({
        title: "Story deleted",
        description: "The story has been removed successfully."
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete story. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast({
      title: "Logged out",
      description: "You have been logged out successfully."
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  const fictionCount = stories.filter(s => s.category === 'fiction').length;
  const articleCount = stories.filter(s => s.category === 'article').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-serif text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center gap-4">
              <a href="/" className="text-sm text-gray-600 hover:text-gray-900">View Site</a>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-12">
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen size={20} className="text-gray-600" />
              <h3 className="text-sm uppercase tracking-wider text-gray-600">Fiction</h3>
            </div>
            <p className="text-3xl font-serif text-gray-900">{fictionCount}</p>
          </div>
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText size={20} className="text-gray-600" />
              <h3 className="text-sm uppercase tracking-wider text-gray-600">Articles</h3>
            </div>
            <p className="text-3xl font-serif text-gray-900">{articleCount}</p>
          </div>
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Book size={20} className="text-gray-600" />
              <h3 className="text-sm uppercase tracking-wider text-gray-600">Novels</h3>
            </div>
            <p className="text-3xl font-serif text-gray-900">{novels.length}</p>
          </div>
          <Link to="/admin/author" className="bg-white border border-gray-200 p-6 hover:border-gray-400 transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <User size={20} className="text-gray-600" />
              <h3 className="text-sm uppercase tracking-wider text-gray-600">Author Info</h3>
            </div>
            <p className="text-sm text-gray-600">Edit profile</p>
          </Link>
          <Link to="/admin/contacts" className="bg-white border border-gray-200 p-6 hover:border-gray-400 transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <Mail size={20} className="text-gray-600" />
              <h3 className="text-sm uppercase tracking-wider text-gray-600">Messages</h3>
            </div>
            <p className="text-sm text-gray-600">View submissions</p>
          </Link>
        </div>

        {/* Novels Management */}
        <div className="bg-white border border-gray-200 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif text-gray-900">Novels</h2>
            <Link to="/admin/novels">
              <Button variant="outline" className="text-sm">
                Manage All Novels →
              </Button>
            </Link>
          </div>
          {novels.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No novels yet. Create your first novel!</p>
          ) : (
            <div className="space-y-3">
              {novels.slice(0, 3).map((novel) => (
                <div key={novel.id} className="border border-gray-200 p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-gray-900">{novel.title}</h3>
                    <p className="text-sm text-gray-500">{novel.totalChapters} chapters · {novel.status}</p>
                  </div>
                  <Link to={`/admin/novel/chapters/${novel.id}`}>
                    <Button variant="outline" size="sm">Manage</Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stories Management */}
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-serif text-gray-900">Manage Stories</h2>
            <Link to="/admin/story/new">
              <Button className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800">
                <Plus size={16} />
                Add New Story
              </Button>
            </Link>
          </div>

          {/* Filter */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setFilter('all')}
              className={`text-sm uppercase tracking-wider pb-2 border-b-2 transition-colors ${
                filter === 'all' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              All ({stories.length})
            </button>
            <button
              onClick={() => setFilter('fiction')}
              className={`text-sm uppercase tracking-wider pb-2 border-b-2 transition-colors ${
                filter === 'fiction' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Fiction ({fictionCount})
            </button>
            <button
              onClick={() => setFilter('article')}
              className={`text-sm uppercase tracking-wider pb-2 border-b-2 transition-colors ${
                filter === 'article' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Articles ({articleCount})
            </button>
          </div>

          {/* Stories List */}
          <div className="space-y-4">
            {stories.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No stories found. Add your first story!</p>
            ) : (
              stories.map((story) => (
                <div key={story.id} className="border border-gray-200 p-6 hover:border-gray-400 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-serif text-gray-900">{story.title}</h3>
                        <span className="text-xs uppercase tracking-wider px-2 py-1 bg-gray-100 text-gray-600">
                          {story.category}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{story.excerpt.substring(0, 150)}...</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{story.date}</span>
                        <span>{story.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-6">
                      <Link to={`/admin/story/edit/${story.id}`}>
                        <Button variant="outline" size="sm" className="flex items-center gap-2">
                          <Edit size={14} />
                          Edit
                        </Button>
                      </Link>
                      <Button
                        onClick={() => handleDelete(story.id, story.title)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-600"
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;